import axios from "axios";

const PROD_BASE_URL = "https://nexthire.reyan.co.in/reyanjobs";
const TEST_BASE_URL = "https://test-jobs.reyan.co.in/reyanjobs";
// const DEV_BASE_URL = "https://dev-nexthire.reyan.co.in/reyanjobs";

const FALLBACK_BASE_URL = "http://localhost:5001/reyanjobs";
const DEFAULT_BASE_URL = (PROD_BASE_URL || FALLBACK_BASE_URL).replace(/\/+$/, "");

// AuthContext replaces this at runtime so refresh failures can log the user out.
let authFailureHandler = async () => {};

function configureClient(api, baseURL = DEFAULT_BASE_URL) {
  api.defaults.baseURL = baseURL;
  api.defaults.withCredentials = true;
  api.defaults.headers.common["Content-Type"] = "application/json";
}

function attachRefreshInterceptor(api, onAuthFailure = async () => {}) {
  let refreshPromise = null;

  async function refreshSession() {
    // Deduplicate concurrent 401s so they all wait on the same refresh request.
    if (!refreshPromise) {
      refreshPromise = api.post("/refresh").finally(() => {
        refreshPromise = null;
      });
    }

    return refreshPromise;
  }

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      const isRefreshRequest = originalRequest.url?.includes("/refresh");
      const isAuthRoute =
        originalRequest.url?.includes("/login") ||
        originalRequest.url?.includes("/logout") ||
        originalRequest.url?.includes("/logout-all");

      // Retry protected requests once after refreshing the access token.
      if (status === 401 && !originalRequest._retry && !isRefreshRequest && !isAuthRoute) {
        originalRequest._retry = true;

        try {
          await refreshSession();
          return api(originalRequest);
        } catch (refreshError) {
          await onAuthFailure(refreshError);
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
}

export function setAuthFailureHandler(handler = async () => {}) {
  authFailureHandler = handler;
}

export function createApiClient({
  baseURL = DEFAULT_BASE_URL,
  onAuthFailure = async () => {}
} = {}) {
  // Use a dedicated axios instance for auth helpers so they can be consumed as a small API surface.
  const api = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json"
    }
  });

  attachRefreshInterceptor(api, onAuthFailure);

  return {
    api,

    login(payload) {
      return api.post("/login", payload);
    },

    register(payload, type) {
      return api.post(`/register?type=${encodeURIComponent(type)}`, payload);
    },

    registerCandidate(payload) {
      return api.post("/register-candidate?type=candidate", payload);
    },

    getProfile() {
      return api.get("/profile");
    },

    refresh() {
      return api.post("/refresh");
    },

    logout() {
      return api.post("/logout");
    },

    logoutAll() {
      return api.post("/logout-all");
    }
  };
}

// Configure the global axios client because most existing API modules still import axios directly.
configureClient(axios, DEFAULT_BASE_URL);
attachRefreshInterceptor(axios, async (error) => authFailureHandler(error));

export const api = axios;

// Auth-specific convenience wrapper used by JWTAuthContext.
export const authClient = createApiClient({
  baseURL: DEFAULT_BASE_URL,
  onAuthFailure: async (error) => authFailureHandler(error)
});
